import React, { useEffect, useState } from "react";
import { Modal, FormActions, Input } from "../../shared";
import { appointmentsApi } from "../../../services/appointmentsApi";
import { toAppointmentDateOnly } from "../../../utils/appointmentUtils";
import { addDaysToYmd, formatDateShort } from "../../../utils/dateUtils";

interface AppointmentEndModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: number;
  appointmentDate: string;
  initialAppointmentEnd?: string | null;
  onSuccess: () => void;
}

export const AppointmentEndModal: React.FC<AppointmentEndModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  appointmentDate,
  initialAppointmentEnd,
  onSuccess,
}) => {
  const startDate = toAppointmentDateOnly(appointmentDate);
  const savedEnd = toAppointmentDateOnly(initialAppointmentEnd);
  const hasSavedEnd = Boolean(savedEnd && savedEnd > startDate);

  const [enabled, setEnabled] = useState(hasSavedEnd);
  const [appointmentEnd, setAppointmentEnd] = useState(
    hasSavedEnd ? savedEnd : addDaysToYmd(startDate, 1)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const start = toAppointmentDateOnly(appointmentDate);
    const end = toAppointmentDateOnly(initialAppointmentEnd);
    const hasEnd = Boolean(end && end > start);
    setEnabled(hasEnd);
    setAppointmentEnd(hasEnd ? end : addDaysToYmd(start, 1));
    setError(null);
  }, [isOpen, appointmentDate, initialAppointmentEnd]);

  const handleToggle = () => {
    setEnabled((prev) => {
      const next = !prev;
      if (next && !appointmentEnd) {
        setAppointmentEnd(addDaysToYmd(startDate, 1));
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (enabled) {
      if (!appointmentEnd) {
        setError("Informe a data de término");
        return;
      }
      if (appointmentEnd <= startDate) {
        setError("A data de término deve ser posterior à data do agendamento");
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const response = await appointmentsApi.updateAppointment(appointmentId, {
        appointmentEnd: enabled ? appointmentEnd : null,
      });

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.error || "Falha ao salvar a data de término");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao salvar a data de término"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Estender agendamento"
      size="sm"
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <p className="text-sm text-gray-600">
          Data inicial:{" "}
          <span className="font-medium text-gray-900">
            {formatDateShort(startDate)}
          </span>
        </p>

        <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Permanecer por mais dias
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                O mesmo agendamento aparece em todos os dias até a data de
                término.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggle}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 self-start sm:self-auto items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                enabled ? "bg-sky-600" : "bg-gray-300"
              }`}
              title={
                enabled
                  ? "Remover data de término"
                  : "Definir data de término"
              }
              aria-pressed={enabled}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <Input
            label="Data de término"
            type="date"
            value={appointmentEnd}
            onChange={(e) => setAppointmentEnd(e.target.value)}
            disabled={!enabled}
          />
        </div>

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={hasSavedEnd}
          submitText="Salvar"
          submitType="button"
        />
      </div>
    </Modal>
  );
};
